import { motion } from "framer-motion";
import { ArrowRight, Sparkles, ShieldCheck, Clock3, MapPin, CheckCircle2 } from "lucide-react";
import SafeImage from "@/components/SafeImage";
import { IMAGES } from "@/data/images";
import { DIFFERENTIATORS } from "@/data/partners";

const stats = [
  { icon: MapPin, value: "Beograd", label: "Zona preuzimanja i dostave" },
  { icon: Clock3, value: "24 – 48h", label: "Standardni rok obrade" },
  { icon: ShieldCheck, value: "B2B", label: "Ekskluzivno za biznis" },
];

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="hero-luxury relative w-full overflow-hidden"
    >
      <div className="absolute inset-0">
        <SafeImage
          src={IMAGES.hero.background}
          alt={IMAGES.hero.backgroundAlt}
          className="h-full w-full object-cover object-center"
          loading="eager"
          fetchPriority="high"
          sizes="100vw"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#030812] via-[#030812]/94 to-[#030812]/60 md:to-[#030812]/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030812] via-transparent to-[#030812]/50" />
        <div className="absolute inset-0 hero-grain opacity-30" />
      </div>

      <div
        className="glow-blob hidden md:block"
        style={{
          top: "5%",
          right: "5%",
          width: 480,
          height: 480,
          background: "radial-gradient(circle, #1fa8ff 0%, transparent 65%)",
          opacity: 0.35,
        }}
      />

      <div className="relative z-10 tw-container flex flex-col justify-center pt-[4.75rem] pb-8 sm:pt-28 sm:pb-10 md:pt-32 md:pb-12 lg:min-h-[calc(100svh-5.25rem)] lg:pt-36">
        <div className="grid grid-cols-1 items-center gap-6 sm:gap-8 lg:grid-cols-12 lg:gap-12 xl:gap-16">
          <div className="order-2 lg:order-1 lg:col-span-6 xl:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="hero-badge mb-4 sm:mb-5 md:mb-6"
            >
              <Sparkles size={14} className="text-[#55d0ff] shrink-0" strokeWidth={2} />
              <span className="leading-snug">
                #1 B2B perionica · Beograd · Rakovica
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="hero-title"
            >
              Profesionalno pranje tekstila
              <span className="hero-title-accent"> za restorane, hotele i spa centre </span>
              u Beogradu.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="mt-4 sm:mt-5 md:mt-6 max-w-xl text-[#b8c9e4] text-sm sm:text-base md:text-lg leading-relaxed"
            >
              <strong className="text-white font-semibold">TOP WASH DOO</strong> je specijalizovana
              industrijska perionica za poslovne klijente — hotelsku posteljinu, stolnjake, peškire,
              uniforme i spa tekstil. Fiksni raspored preuzimanja i isporuke, bez skrivenih troškova.
            </motion.p>

            <motion.ul
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.32 }}
              className="hero-usps mt-4 sm:mt-5 md:mt-6"
              aria-label="Zašto TOP WASH DOO"
            >
              {DIFFERENTIATORS.slice(0, 4).map((item) => (
                <li key={item} className="hero-usp">
                  <CheckCircle2 size={15} className="text-[#55d0ff] shrink-0 mt-0.5" strokeWidth={2.5} />
                  {item}
                </li>
              ))}
            </motion.ul>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.38 }}
              className="mt-5 sm:mt-6 md:mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4"
            >
              <a
                href="#contact"
                className="tw-btn w-full justify-center sm:w-auto min-h-[48px]"
                data-testid="hero-cta-button"
              >
                Besplatna procena <ArrowRight size={16} strokeWidth={2.5} />
              </a>
              <a
                href="#services"
                className="tw-btn-ghost w-full justify-center sm:w-auto min-h-[48px]"
                data-testid="hero-explore-button"
              >
                Pogledajte usluge
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="order-1 lg:order-2 lg:col-span-6 xl:col-span-7 w-full max-w-[340px] sm:max-w-md mx-auto lg:max-w-none lg:mx-0"
          >
            <div className="hero-mosaic hero-mosaic--compact">
              {IMAGES.hero.mosaic.map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.3 + i * 0.1 }}
                  className={`hero-mosaic-item hero-mosaic-item--${i + 1}`}
                >
                  <SafeImage
                    src={item.src}
                    alt={item.alt}
                    loading="lazy"
                    sizes="(max-width: 640px) 45vw, 280px"
                  />
                  <div className="hero-mosaic-overlay" />
                  <span className="hero-mosaic-label">{item.label}</span>
                </motion.div>
              ))}
              <div className="hero-mosaic-frame" aria-hidden />
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.55 }}
          className="mt-6 sm:mt-8 md:mt-12 lg:mt-14"
        >
          <div className="hero-stats">
            {stats.map(({ icon: Icon, value, label }) => (
              <div key={label} className="hero-stat">
                <div className="hero-stat-icon">
                  <Icon size={18} strokeWidth={1.8} className="text-[#a7e2ff]" />
                </div>
                <div className="min-w-0">
                  <div className="font-heading text-base sm:text-xl md:text-2xl text-white">
                    {value}
                  </div>
                  <div className="text-[10px] sm:text-xs uppercase tracking-[0.12em] sm:tracking-[0.2em] text-[#8ea6c8] mt-0.5 sm:mt-1 font-semibold leading-snug">
                    {label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      <div className="relative z-10 border-t border-[#55d0ff]/12 bg-[#030812]/80 backdrop-blur-xl overflow-hidden">
        <div className="flex whitespace-nowrap tw-marquee-track py-2.5 sm:py-4 md:py-5">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 sm:gap-10 md:gap-14 pr-4 sm:pr-10 md:pr-14 shrink-0">
              {[
                "Profesionalno pranje veša Beograd",
                "Perionica za hotele",
                "Pranje stolnjaka restoran",
                "Hotelska posteljina",
                "Peškiri i spa frotir",
                "Kuvarske uniforme",
                "B2B saradnja",
                "Besplatna procena",
              ].map((w) => (
                <span
                  key={w + i}
                  className="font-heading-md text-sm sm:text-xl md:text-2xl lg:text-3xl text-white/80"
                >
                  {w} <span className="text-[#55d0ff] mx-1.5 sm:mx-3">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
